import { ABTestConfig } from "../types/ABTest";

// LIVE A/B tests only. abTestingService.getActiveVariants() stamps ONLY testIds
// that appear here, so removing a concluded test from this file is what STOPS it
// being written onto new quotes/drafts (and a one-time data strip clears the
// historical stamps — see scripts/cleanup_stale_ab_tests.ts). Concluded tests'
// results live in the ABTestSnapshot collection (dashboard → Archived), not here.
//
// Retired 2026-06-18 (removed from here + data stripped):
//   • patient-portal-link-v1 — hidden-link feature flag (always control; the
//     portal link was never shown — its dead conditionals were removed too).
//   • financing-forward-v1   — treatment won; snapshotted, served 100% in code.
//   • procedure-focus-v1     — focused won; served 100% in code.
// Retired 2026-06-20 (removed from here; data strip pending):
//   • contact-sweeps-v1      — "no_sweeps" won; sweepstakes framing removed for
//     everyone (StepContact hideSweeps = true). Run
//     scripts/cleanup_stale_ab_tests.ts --prod --run to clear historical stamps.
// Retired 2026-07-18 (removed from here + data stripped):
//   • free-evaluation-cta-v1 — treatment won (+44% Final-CTA); snapshotted +
//     resolution overlay active (dashboard → Archived); "Free evaluation" CTA
//     now shown to ALL mobile users (served 100% in code in
//     QuoteSummaryTreatment). Stamps stripped post-deploy 2026-07-18; the same
//     run also cleared signature-price-v1's residual stamps (see below).
// Retired 2026-07-28 (removed from here; data strip pending):
//   • quote-cap-no-price-v1 — no_price won; snapshotted + resolution overlay
//     active (dashboard → Archived); over-cap no-price mode (PriceCapPanel)
//     now served 100% in code (QuoteSummaryTreatment + StepPhotos welcome
//     modal). Run scripts/cleanup_stale_ab_tests.ts --prod --run post-deploy
//     to strip historical stamps.
// Retired 2026-08-25 (removed from here; data strip pending):
//   • photo-gated-price-v1 — "photo_gate" won; the ESTIMATE-ONLY funnel is now
//     served 100% in code. No price figure is rendered anywhere in the funnel,
//     photos are required, and the coordinator sends the official estimate
//     (24h; ~1h once the patient shows payment intent). Flattened at every call
//     site: QuoteSummaryTreatment + StepPhotos lost their enrollment hooks and
//     all control-only rendering (priced welcome modal, "−25% OFF" pill,
//     discount-unlocked copy); server-side derivePhotoGateState /
//     computeTodaysPrice / quoteReady / the ESTIMATE DUE lane predicates no
//     longer read the arm stamp (server/shared/abTests.ts keeps the id +
//     variant constants for history). Run
//     scripts/cleanup_stale_ab_tests.ts --prod --run post-deploy to strip
//     historical stamps.
// Retired 2026-08-27 (removed from here; data strip pending):
//   • signature-price-v2 — "hide_price" won (owner call); snapshotted +
//     resolution overlay active (dashboard → Archived). The Signature Card
//     up-front price is now gone for EVERYONE — hardwired in
//     SignatureProcedureCard (enrollment hook, calcCardPrice and the
//     FinancingHero price block all removed). Run
//     scripts/cleanup_stale_ab_tests.ts --prod --run post-deploy to strip
//     historical stamps.
//   • lp-luxury-cta-copy-v1 — retired IRRELEVANT, no winner (underpowered:
//     ~7 sessions / ~1 submission in 9 days); the Luxury Concierge LP was
//     consolidated 2026-08-26 to one hardcoded CTA, so neither arm exists
//     any more. PHP registry entry (ab_config.php) is already status
//     'finished' (inert pinned winner) and expires the ta_ab_* cookie on LP
//     visits; deleting this mirror stops stale cookies stamping new quotes.
//     Same cleanup run strips its stamps.

// Landing-page tests: assignment happens SERVER-SIDE in PHP
// (sites/topaesthetics.com/assets/inc/ab_config.php) — the funnel NEVER
// assigns these (enabled: false); this entry only whitelists the testId so
// getActiveVariants() stamps the PHP-set ta_ab_* cookie onto Quotes.
// Weights/variants here must MIRROR the PHP registry entry.
//
// export const LP_HERO_COPY_AB_TEST: ABTestConfig = {
//   testId: "lp-hero-copy-v1",
//   name: "Landing: hero copy (topaesthetics.com)",
//   enabled: false,
//   status: "active",
//   showInPatientUI: false,
//   surface: "landing",
//   variants: {
//     control: { name: "Current hero", weight: 0.5 },
//     b: { name: "Variant B hero", weight: 0.5 },
//   },
// };

// Landing tests (assignment SERVER-SIDE in PHP, ab_config.php) mirror here
// with enabled:false ONLY so KNOWN_TEST_IDS admits the testId and
// getActiveVariants() stamps the PHP-set ta_ab_* cookie onto
// Quote/DraftQuote.activeABTests. Deleting the mirror is the retirement
// switch that stops stale 365-day ta_ab_* cookies stamping a finished test's
// old arm onto new quotes (the PHP side also expires those cookies on LP
// visits). lp-standalone-motiva-v1 + lp-motiva-cta-dest-v1 FINISHED
// 2026-08-13 (winners: standalone / general) — mirrors deleted accordingly.

// Lipo 360 LP style skin (Miami violet vs Luxe champagne) — assignment lives
// in the PHP registry; this mirror only lets the ta_ab_* cookie stamp quotes.
export const LIPO360_STYLE_AB_TEST: ABTestConfig = {
  testId: "lipo360-style-v1",
  name: "Landing: Lipo 360 style skin (miami vs luxe)",
  enabled: false,
  status: "active",
  showInPatientUI: false,
  surface: "landing",
  variants: {
    miami: { name: "Miami (violet/mono, shipped 2026-09-10)", weight: 0.5 },
    luxe: { name: "Luxe (Cormorant/champagne, house luxury)", weight: 0.5 },
  },
};

// BBL LP redesign (control vs the "editorial" daylight arm, owner approved
// 2026-10-05). Assignment lives in the PHP registry; this mirror only lets
// the ta_ab_* cookie stamp quotes. Spec + read/stop rule:
// docs/marketing/landing-pages/bbl-editorial-v1.md.
export const BBL_EDITORIAL_AB_TEST: ABTestConfig = {
  testId: "bbl-editorial-v1",
  name: "Landing: BBL editorial redesign (control vs editorial)",
  enabled: false,
  status: "active",
  showInPatientUI: false,
  surface: "landing",
  variants: {
    control: { name: "Control (shipped violet page, price first)", weight: 0.5 },
    editorial: { name: "Editorial (daylight, surgeon first, Free Evaluation leads)", weight: 0.5 },
  },
};

export const AB_TEST_CONFIGS: Record<string, ABTestConfig> = {
  [LIPO360_STYLE_AB_TEST.testId]: LIPO360_STYLE_AB_TEST,
  [BBL_EDITORIAL_AB_TEST.testId]: BBL_EDITORIAL_AB_TEST,
};
